import { useEffect, useState } from 'react';
import { Icona } from '../../components/Icona';
import { scontrini } from '../../api/scontrini';
import type { DettaglioScontrino, Pagamento, PreparazioneScontrino } from '../../api/scontrini';
import { formattaImporto } from '../../lib/formato';
import { numeroDecimale } from '../../lib/numeri';

/*  LO SCONTRINO DELL'INTERVENTO, dal telefono del tecnico caldaie (22/10/2026).

    Tre articoli col prezzo del listino di Mago, la quantita', contanti o POS, la mail
    del cliente. Il prezzo il listino lo PROPONE e il tecnico lo puo' cambiare (22/10/2026);
    sullo scontrino resta scritto anche quello proposto.
    Oltre i 500 € in contanti il bottone si spegne e dice perche' — lo stesso blocco lo
    fa il server, questo serve a non far scoprire il limite dopo aver premuto.

    Premuto «Emetti», lo scontrino si scrive e va alla stampante di Novara. Se la
    stampante non risponde lo scontrino non si perde: resta da emettere, e si riprova.  */
export function NuovoScontrino({ onIncassi }: { onIncassi: () => void }) {
  const [prep, setPrep] = useState<PreparazioneScontrino | null>(null);
  const [quantita, setQuantita] = useState<Record<string, number>>({});
  /*  Il prezzo come lo sta scrivendo il tecnico, in testo: «12,» a meta' battitura non e'
      ancora un numero, e convertirlo a ogni tasto gli toglierebbe la virgola di mano.  */
  const [prezzi, setPrezzi] = useState<Record<string, string>>({});
  const [pagamento, setPagamento] = useState<Pagamento | null>(null);
  const [mail, setMail] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [esito, setEsito] = useState<DettaglioScontrino | null>(null);

  const carica = () => {
    setErrore(null);
    scontrini
      .prepara()
      .then((p) => {
        setPrep(p);
        setPrezzi(
          Object.fromEntries(
            p.articoli.map((a) => [a.codice, a.prezzo === null ? '' : a.prezzo.toFixed(2).replace('.', ',')]),
          ),
        );
      })
      .catch((e: Error) => setErrore(e.message));
  };
  useEffect(carica, []);

  if (esito) {
    return (
      <EsitoScontrino
        scontrino={esito}
        onCambiato={setEsito}
        onNuovo={() => {
          setEsito(null);
          setQuantita({});
          setPagamento(null);
          setMail('');
          carica();
        }}
        onIncassi={onIncassi}
      />
    );
  }

  const prezzoDi = (codice: string) => numeroDecimale(prezzi[codice]);
  const totale = (prep?.articoli ?? []).reduce(
    (s, a) => s + (prezzoDi(a.codice) ?? 0) * (quantita[a.codice] ?? 0),
    0,
  );
  const senzaPrezzo = (prep?.articoli ?? []).some(
    (a) => (quantita[a.codice] ?? 0) > 0 && !((prezzoDi(a.codice) ?? 0) > 0),
  );
  const troppiContanti = pagamento === 'Contanti' && prep !== null && totale > prep.limiteContanti;
  const pronto =
    prep?.siPuoEmettere === true && totale > 0 && pagamento !== null && !troppiContanti && !senzaPrezzo;

  const cambia = (codice: string, di: number) =>
    setQuantita((q) => ({ ...q, [codice]: Math.max(0, Math.min(99, (q[codice] ?? 0) + di)) }));

  async function emetti() {
    if (!pronto || pagamento === null) return;
    setInCorso(true);
    setErrore(null);
    try {
      const nato = await scontrini.crea({
        righe: Object.entries(quantita)
          .filter(([, q]) => q > 0)
          .map(([codiceArticolo, q]) => ({ codiceArticolo, quantita: q, prezzoUnitario: prezzoDi(codiceArticolo) })),
        pagamento,
        mailCliente: mail.trim() === '' ? null : mail.trim(),
      });
      setEsito(nato);
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  return (
    <div className="rapportino scontrino">
      <header className="testata-mobile">
        <h1>Nuovo scontrino</h1>
      </header>

      {prep === null && !errore && <p className="nota">Leggo i prezzi da Mago…</p>}
      {prep?.avviso && <p className="messaggio">{prep.avviso}</p>}

      {prep && (
        <section>
          {prep.articoli.map((a) => (
            <div key={a.codice} className="riga-ore">
              <div className="chi">
                <strong>{a.descrizione}</strong>
                <label className="prezzo">
                  <input
                    inputMode="decimal"
                    aria-label={`Prezzo di ${a.descrizione}`}
                    value={prezzi[a.codice] ?? ''}
                    placeholder="prezzo"
                    onChange={(e) => setPrezzi((p) => ({ ...p, [a.codice]: e.target.value }))}
                  />
                  €
                </label>
                <small>
                  {a.prezzo === null
                    ? 'il listino non lo propone'
                    : prezzoDi(a.codice) !== a.prezzo
                      ? `listino ${formattaImporto(a.prezzo)}`
                      : a.codice}
                </small>
              </div>
              <div className="contatore">
                <button
                  type="button"
                  aria-label={`Togli ${a.descrizione}`}
                  disabled={(quantita[a.codice] ?? 0) === 0}
                  onClick={() => cambia(a.codice, -1)}
                >
                  <Icona nome="meno" />
                </button>
                <output>{quantita[a.codice] ?? 0}</output>
                <button
                  type="button"
                  aria-label={`Aggiungi ${a.descrizione}`}
                  onClick={() => cambia(a.codice, 1)}
                >
                  <Icona nome="piu" />
                </button>
              </div>
              <span className="importo">
                {formattaImporto((prezzoDi(a.codice) ?? 0) * (quantita[a.codice] ?? 0))}
              </span>
            </div>
          ))}
          <p className="totale-ore">
            Totale <strong>{formattaImporto(totale)}</strong>{' '}
            <span className="nota">IVA {prep.aliquotaIva}% compresa</span>
          </p>
        </section>
      )}

      {prep && (
        <section>
          <span className="etichetta">Pagamento</span>
          <div className="scelta-pagamento" role="group" aria-label="Pagamento">
            <button
              type="button"
              className={pagamento === 'Contanti' ? 'on' : ''}
              aria-pressed={pagamento === 'Contanti'}
              onClick={() => setPagamento('Contanti')}
            >
              Contanti
            </button>
            <button
              type="button"
              className={pagamento === 'Pos' ? 'on' : ''}
              aria-pressed={pagamento === 'Pos'}
              onClick={() => setPagamento('Pos')}
            >
              POS
            </button>
          </div>
          {troppiContanti && (
            <p className="errore">
              In contanti si incassa al massimo {formattaImporto(prep.limiteContanti)}. Servono i dati del cliente
              per fare la fattura dall’ufficio, oppure si paga col POS.
            </p>
          )}
        </section>
      )}

      {prep && (
        <label className="campo-blocco">
          <span>Mail del cliente</span>
          <input
            type="email"
            inputMode="email"
            autoComplete="off"
            placeholder="per mandargli lo scontrino"
            value={mail}
            onChange={(e) => setMail(e.target.value)}
          />
          <small className="nota">
            {mail.trim() === ''
              ? prep.mailTecnico
                ? `Senza mail la copia arriva a te (${prep.mailTecnico}) da stampare in sede.`
                : 'Senza mail del cliente la copia andrebbe a te, ma non hai una mail in anagrafica: chiedila all’ufficio.'
              : 'Il cliente riceve la copia dello scontrino.'}
          </small>
        </label>
      )}

      {errore && <p className="errore">{errore}</p>}

      <button type="button" className="salva" disabled={!pronto || inCorso} onClick={() => void emetti()}>
        {inCorso ? 'Emetto…' : `Emetti ${totale > 0 ? formattaImporto(totale) : ''}`}
      </button>
    </div>
  );
}

/** Com'è andata: emesso, da riprovare, o da verificare sulla stampante. */
function EsitoScontrino({
  scontrino,
  onCambiato,
  onNuovo,
  onIncassi,
}: {
  scontrino: DettaglioScontrino;
  onCambiato: (s: DettaglioScontrino) => void;
  onNuovo: () => void;
  onIncassi: () => void;
}) {
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  async function riprova() {
    setInCorso(true);
    setErrore(null);
    try {
      onCambiato(await scontrini.riprova(scontrino.id));
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  return (
    <div className="rapportino scontrino">
      <header className="testata-mobile">
        <h1>Scontrino {scontrino.numero}</h1>
      </header>

      {scontrino.stato === 'Emesso' && (
        <p className="esito-scontrino ok">
          Emesso: documento n. {scontrino.numeroDocumento ?? '—'}, {formattaImporto(scontrino.totale)} in{' '}
          {scontrino.pagamento === 'Contanti' ? 'contanti' : 'POS'}.
          {scontrino.mailCopia && <> La copia va a {scontrino.mailCopia}.</>}
        </p>
      )}
      {scontrino.stato === 'DaEmettere' && (
        <p className="esito-scontrino ko">
          Non emesso: {scontrino.ultimoErrore ?? 'la stampante non ha risposto.'} Lo scontrino è salvato: si può
          riprovare.
        </p>
      )}
      {scontrino.stato === 'DaVerificare' && (
        <p className="esito-scontrino ko">
          La stampante non ha risposto in tempo e potrebbe averlo emesso. Non riprovare: avvisa l’ufficio, che
          controlla sulla stampante di Novara.
        </p>
      )}

      <section>
        {scontrino.righe.map((r) => (
          <div key={r.codiceArticolo} className="riga-materiale">
            <span className="desc">{r.descrizione}</span>
            <span className="qta">
              {r.quantita} × {formattaImporto(r.prezzoUnitario)}
              {r.prezzoListino !== null && r.prezzoListino !== r.prezzoUnitario && (
                <small className="listino"> (listino {formattaImporto(r.prezzoListino)})</small>
              )}
            </span>
            <span className="qta">{formattaImporto(r.importo)}</span>
          </div>
        ))}
      </section>

      {errore && <p className="errore">{errore}</p>}

      {scontrino.stato === 'DaEmettere' && (
        <button type="button" className="salva" disabled={inCorso} onClick={() => void riprova()}>
          {inCorso ? 'Riprovo…' : 'Riprova'}
        </button>
      )}
      <p className="azioni">
        <button type="button" className="primario" onClick={onNuovo}>
          Nuovo scontrino
        </button>
        <button type="button" onClick={onIncassi}>
          I miei incassi
        </button>
      </p>
    </div>
  );
}
